// src/components/Leaderboard.tsx
import React, { useState, useEffect } from 'react';
import { getTopScores, getUserScores, LeaderboardEntry } from '../services/LeaderboardService.ts';
import { useGame } from '../context/GameContext';
import { Shield, RefreshCw }  from '../components/icons';

interface LeaderboardProps {
  filter?: string;
  showUserTab?: boolean;
}

const Leaderboard: React.FC<LeaderboardProps> = ({ 
  filter = 'all',
  showUserTab = true
}) => {
  const [topScores, setTopScores] = useState<LeaderboardEntry[]>([]);
  const [userScores, setUserScores] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [tab, setTab] = useState<'top' | 'user'>('top');
  const [error, setError] = useState<string | null>(null);
  const { state } = useGame();

  useEffect(() => {
    const fetchScores = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Fetch top scores
        const topResult = await getTopScores(filter);
        if (topResult.success && topResult.data) {
          setTopScores(topResult.data);
        } else if (topResult.error) {
          setError(topResult.error);
        }
        
        // Fetch user scores if logged in
        if (state.user?.id && showUserTab) {
          const userResult = await getUserScores(state.user.id);
          if (userResult.success && userResult.data) {
            setUserScores(userResult.data);
          }
        }
      } catch (err) {
 
        setError("Failed to load leaderboard data. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    
    fetchScores();
  }, [state.user?.id, filter, showUserTab]);

  // Get class color 
  const getClassColor = (classType: string): string => {
    switch (classType) {
      case 'FIREWALL': return 'text-red-400';
      case 'AZURE': return 'text-blue-400';
      case 'NETWORK': return 'text-green-400';
      case 'PENTESTER': return 'text-purple-400';
      default: return 'text-gray-400';
    }
  };
  
  // Get class icon
  const getClassIcon = (classType: string) => {
    // You can replace this with appropriate icons for each class
    return <Shield className={`h-4 w-4 ${getClassColor(classType)}`} />;
  };
  
  // Format date
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-900/50 border border-red-500 text-red-200 p-4 rounded-md">
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div>
      {/* Tabs - Only show if user tab is enabled and user is logged in */}
      {showUserTab && state.user && (
        <div className="flex border-b border-gray-700 mb-4">
          <button
            className={`py-2 px-4 ${tab === 'top' ? 'text-yellow-400 border-b-2 border-yellow-400' : 'text-gray-400'}`}
            onClick={() => setTab('top')}
          >
            Top Scores
          </button>
          
          <button
            className={`py-2 px-4 ${tab === 'user' ? 'text-yellow-400 border-b-2 border-yellow-400' : 'text-gray-400'}`}
            onClick={() => setTab('user')}
          >
            Your Scores
          </button>
        </div>
      )}
      
      {/* Top Scores */}
      {(tab === 'top' || !showUserTab || !state.user) && (
        <>
          {topScores.length === 0 ? (
            <div className="text-center py-10 text-gray-400">
              No scores available. Be the first to submit a score!
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-700">
                    <th className="py-2 px-4 text-left">Rank</th>
                    <th className="py-2 px-4 text-left">Player</th>
                    <th className="py-2 px-4 text-right">Score</th>
                    <th className="py-2 px-4 text-right">Level</th>
                    <th className="py-2 px-4 text-left">Class</th>
                    <th className="py-2 px-4 text-left">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {topScores.map((score, index) => (
                    <tr 
                      key={score.id} 
                      className={`border-b border-gray-700 ${state.user?.id === score.user_id ? 'bg-blue-900/20' : ''}`}
                    >
                      <td className="py-2 px-4">{index + 1}</td>
                      <td className="py-2 px-4">
                        {score.username}
                      </td>
                      <td className="py-2 px-4 text-right text-yellow-300">{score.score.toLocaleString()}</td>
                      <td className="py-2 px-4 text-right">{score.level}</td>
                      <td className="py-2 px-4">
                        <div className="flex items-center">
                          {getClassIcon(score.class_type)}
                          <span className={`ml-2 ${getClassColor(score.class_type)}`}>
                            {score.class_type}
                          </span>
                        </div>
                      </td>
                      <td className="py-2 px-4 text-xs text-gray-400">{formatDate(score.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
      
      {/* User Scores */}
      {tab === 'user' && state.user && showUserTab && (
        <>
          {userScores.length === 0 ? (
            <div className="text-center py-10 text-gray-400">
              You haven't submitted any scores yet. Play the game to see your scores here!
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-700">
                    <th className="py-2 px-4 text-right">Score</th>
                    <th className="py-2 px-4 text-right">Level</th>
                    <th className="py-2 px-4 text-left">Class</th>
                    <th className="py-2 px-4 text-left">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {userScores.map((score) => (
                    <tr key={score.id} className="border-b border-gray-700">
                      <td className="py-2 px-4 text-right text-yellow-300">{score.score.toLocaleString()}</td>
                      <td className="py-2 px-4 text-right">{score.level}</td>
                      <td className="py-2 px-4">
                        <div className="flex items-center">
                          {getClassIcon(score.class_type)}
                          <span className={`ml-2 ${getClassColor(score.class_type)}`}>
                            {score.class_type}
                          </span>
                        </div>
                      </td>
                      <td className="py-2 px-4 text-xs text-gray-400">{formatDate(score.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Leaderboard;